// src/ProfilePage.js
import React, { useState, useEffect } from 'react';
// --- MODIFIED LINE: Import getUserTradeUrl ---
import { setTradeUrl, getUserTradeUrl } from './api';
import { useAuth } from './AuthContext';

// --- Material UI Imports ---
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Avatar from '@mui/material/Avatar';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Link from '@mui/material/Link'; // For Steam profile link
import { alpha } from '@mui/system';

function ProfilePage() {
    const { user } = useAuth(); // Removed refreshUser for now, add back if needed
    const [tradeUrlInput, setTradeUrlInput] = useState('');
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    // --- ADDED STATE: Loading indicator for initial fetch ---
    const [pageLoading, setPageLoading] = useState(false);
    // --- NEW: Set Document Title ---
    useEffect(() => {
        document.title = 'SpinnerCS - Profile';
    }, []);
    // --- END NEW ---
    // --- MODIFIED: Fetch current trade URL on mount ---
    useEffect(() => {
        const fetchProfileData = async () => {
            setPageLoading(true); // Start loading indicator
            setError(''); // Clear previous errors
            setMessage(''); // Clear previous messages
            try {
                const currentTradeUrl = await getUserTradeUrl();
                // Check if the fetched URL is not null, undefined, or an empty string
                if (currentTradeUrl && typeof currentTradeUrl === 'string' && currentTradeUrl.trim() !== '') {
                    setTradeUrlInput(currentTradeUrl.trim());
                } else {
                    setTradeUrlInput(''); // Ensure input is empty if no valid URL is fetched
                }
            } catch (err) {
                console.error("Failed to fetch profile data:", err);
                // Don't necessarily set a visible error, maybe just log it
                // setError("Could not load your current trade URL.");
                setTradeUrlInput(''); // Clear input on error
            } finally {
                setPageLoading(false); // Stop loading indicator
            }
        };

        if (user?.isAuthenticated) {
            fetchProfileData();
        } else {
            // Clear fields if user logs out or isn't logged in
            setTradeUrlInput('');
            setMessage('');
            setError('');
        }
    }, [user]); // Rerun if user changes

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!user?.isAuthenticated || !tradeUrlInput.trim()) {
            setError('Please enter a valid trade URL.');
            return;
        };

        setLoading(true); // Use 'loading' for the save operation
        setMessage('');
        setError('');

        try {
            await setTradeUrl(tradeUrlInput.trim());
            setMessage('Trade URL updated successfully!');
            // Keep the input filled to show the saved value
        } catch (err) {
            const errorMessage = err.response?.data?.message || err.response?.data || err.message || 'Failed to update trade URL.';
            setError(errorMessage);
            console.error("Trade URL update error:", err.response || err);
        } finally {
            setLoading(false); // Stop save operation loading
        }
    };

    if (!user?.isAuthenticated) {
        return (
            <Container maxWidth="sm" sx={{ mt: 4 }}>
                <Alert severity="info">Please log in to view your profile.</Alert>
            </Container>
        );
    }

    // Construct Steam profile URL
    const steamProfileUrl = user.steamId ? `https://steamcommunity.com/profiles/${user.steamId}` : '#';

    return (
        <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
            <Paper elevation={3} sx={{
                p: { xs: 2, sm: 3, md: 4 },
                borderRadius: 2,
                backgroundColor: theme => alpha(theme.palette.background.paper, 0.9),
                backdropFilter: 'blur(5px)',
            }}>
                <Typography variant="h4" component="h1" gutterBottom align="center" sx={{ mb: 3 }}>
                    Your Profile
                </Typography>

                {/* --- User Info Section (No changes needed here) --- */}
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} alignItems="center" sx={{ mb: 4 }}>
                    <Avatar
                        alt={user.displayName}
                        src={user.avatarUrl}
                        sx={{ width: 80, height: 80, border: theme => `3px solid ${theme.palette.primary.main}` }}
                    />
                    <Box sx={{ textAlign: { xs: 'center', sm: 'left' } }}>
                        <Typography variant="h5" component="div" sx={{ fontWeight: 'bold' }}>
                            {user.displayName}
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            SteamID: {user.steamId}
                        </Typography>
                        <Link
                            href={steamProfileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            underline="hover"
                            sx={{ fontSize: '0.9rem' }}
                        >
                            View Steam Profile
                        </Link>
                    </Box>
                </Stack>

                {/* --- Trade URL Section --- */}
                <Typography variant="h6" component="h2" gutterBottom sx={{ mt: 4, mb: 2 }}>
                    Set Steam Trade URL
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Ensure your inventory is public and you provide the correct Trade URL to receive items.
                    You can find your Trade URL <Link href="https://steamcommunity.com/id/me/tradeoffers/privacy#trade_offer_access_url" target="_blank" rel="noopener noreferrer" underline="hover">here</Link>.
                </Typography>

                {/* --- MODIFIED: Show loading indicator while fetching URL --- */}
                {pageLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
                        <CircularProgress />
                        <Typography sx={{ ml: 2 }}>Loading trade URL...</Typography>
                    </Box>
                ) : (
                    <Box component="form" onSubmit={handleSubmit} noValidate>
                        <Stack spacing={2} direction={{ xs: 'column', sm: 'row' }} alignItems="flex-start">
                            <TextField
                                fullWidth
                                required
                                id="trade-url"
                                label="Steam Trade URL"
                                type="url"
                                variant="outlined"
                                value={tradeUrlInput} // Value now comes from state, potentially pre-filled
                                onChange={(e) => setTradeUrlInput(e.target.value)}
                                placeholder="https://steamcommunity.com/tradeoffer/new/..."
                                error={!!error && !message} // Show error state only if there's an error and no success message
                                disabled={loading} // Disable only during save operation
                                sx={{ flexGrow: 1 }}
                            />
                            <Button
                                type="submit"
                                variant="contained"
                                color="primary"
                                disabled={loading || !tradeUrlInput.trim()} // Disable during save or if input is empty
                                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
                                sx={{ py: 1.5, px: 3, minWidth: '150px', mt: { xs: 1, sm: 0 } }}
                            >
                                {loading ? 'Saving...' : 'Save URL'}
                            </Button>
                        </Stack>
                    </Box>
                )}

                {/* --- Feedback Messages --- */}
                <Box sx={{ mt: 3 }}>
                    {/* Show success message even if there was a previous error */}
                    {message && <Alert severity="success" variant="filled">{message}</Alert>}
                    {/* Show error only if there's an error AND no success message */}
                    {error && !message && <Alert severity="error" variant="filled">{error}</Alert>}
                </Box>

            </Paper>
        </Container>
    );
}

export default ProfilePage;
